import React, { useState } from 'react';
import { Button, Form, Input, Divider } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { enums } from '../../config';
import AuthLayout, { FormAlert, GoogleButton, responseCode, usePageTitle } from './AuthLayout';

// Server error codes and what to tell the person about each
const LOGIN_ERRORS = {
    [enums.errorCodes.c1]: 'inputErrors.usernameEmailNotFound',
    [enums.errorCodes.c3]: 'inputErrors.wrongPassword',
    [enums.errorCodes.c4]: 'inputErrors.usernameEmailNotFound'
};

const Page = () => {
    const [t] = useTranslation();
    // 'login', 'forgot' (asking for a reset link) or 'sent' (link mailed)
    const [mode, setMode] = useState(enums.formTypes.login);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const isLogin = mode === enums.formTypes.login;
    usePageTitle(isLogin ? t('login') : t('auth.forgotTitle'));

    const switchMode = (next) => {
        setError('');
        setMode(next);
    };

    const submit = (values) => {
        if (loading) return;
        setLoading(true);
        setError('');
        const url = isLogin ? `/users/login` : `/users/forgot`;
        axios.post(url, { username: values.username, password: values.password })
            .then(res => {
                if (isLogin) {
                    localStorage.setItem('auth-token', res.data);
                    window.location = '/admin';
                } else {
                    setMode('sent');
                }
            })
            .catch(err => setError(t(LOGIN_ERRORS[responseCode(err)] || 'auth.genericError')))
            .finally(() => setLoading(false));
    };

    const loginWithGoogle = (credentialResponse) => {
        if (loading) return;
        setLoading(true);
        setError('');
        axios.post(`/users/login/google`, { credential: credentialResponse.credential })
            .then(res => {
                localStorage.setItem('auth-token', res.data);
                window.location = '/admin';
            })
            .catch(() => setError(t('auth.googleFailed')))
            .finally(() => setLoading(false));
    };

    const backToLogin = (
        <button type="button" className="auth-link auth-back" onClick={() => switchMode(enums.formTypes.login)}>
            <ArrowLeftOutlined /> {t('auth.backToLogin')}
        </button>
    );

    if (mode === 'sent') {
        return (
            <AuthLayout title={t('auth.sentTitle')} subtitle={t('sendedToMailForgotLink')} footer={backToLogin} />
        );
    }

    if (!isLogin) {
        return (
            <AuthLayout title={t('auth.forgotTitle')} subtitle={t('auth.forgotSub')} footer={backToLogin}>
                <Form layout="vertical" requiredMark={false} size="large" onFinish={submit} className="normal_login_form">
                    <Form.Item name="username" label={t('usernameOrEmail')}
                        rules={[{ required: true, whitespace: true, message: t('inputErrors.requiredThisField') }]}>
                        <Input autoFocus autoComplete="username" autoCapitalize="none" spellCheck={false} />
                    </Form.Item>
                    <FormAlert message={error} />
                    <Button block loading={loading} type="primary" htmlType="submit" className="login-form-button">
                        {t('auth.sendResetLink')}
                    </Button>
                </Form>
            </AuthLayout>
        );
    }

    return (
        <AuthLayout
            title={t('auth.loginTitle')}
            subtitle={t('auth.loginSub')}
            footer={<>{t('auth.noAccount')} <Link className="auth-link" to="/signup">{t('signup')}</Link></>}
        >
            <Form layout="vertical" requiredMark={false} size="large" onFinish={submit} className="normal_login_form">
                <Form.Item name="username" label={t('usernameOrEmail')}
                    rules={[{ required: true, whitespace: true, message: t('inputErrors.requiredThisField') }]}>
                    <Input autoFocus autoComplete="username" autoCapitalize="none" spellCheck={false} />
                </Form.Item>
                <Form.Item
                    name="password"
                    className="auth-password"
                    label={t('password')}
                    rules={[{ required: true, message: t('inputErrors.requiredThisField') }]}
                >
                    <Input.Password autoComplete="current-password" />
                </Form.Item>
                {/* Outside the label so clicking it never focuses the field instead */}
                <button type="button" className="auth-link auth-forgot" onClick={() => switchMode(enums.formTypes.forget)}>
                    {t('forgotPassword')}
                </button>
                <FormAlert message={error} />
                <Button block loading={loading} type="primary" htmlType="submit" className="login-form-button">
                    {t('login')}
                </Button>
            </Form>
            <Divider plain className="auth-divider">{t('or')}</Divider>
            <GoogleButton text="continue_with" onCredential={loginWithGoogle} onError={() => setError(t('auth.googleFailed'))} />
        </AuthLayout>
    );
};

export default Page;
